import type { Command } from "../document/commands.ts";
import type { MapDocument, TileRef, WorldDocument } from "../document/types.ts";
import type { BrushEntry, BrushSet, BrushSetLookup } from "./brush-set-file.ts";
import { removePropsCommand } from "./many-props.ts";
import { coverSetsOf, entryCover, knownCover } from "./scatter-cover.ts";
import { appendGhostPieces, brushKit, erasableIn, eraseLayers, firstSkipReason, ghostPieces, strokeCommand, type BrushGhost, type HeldKit } from "./prop-brush-stroke.ts";
import type { ReferenceThing } from "./references.ts";
import { DENSITY_MAX, DENSITY_MIN, RADIUS_MAX, RADIUS_MIN } from "./scatter-density.ts";
import type { FrameLookups, ScatterKit } from "./scatter-entries.ts";
import { createOccupancy, markOccupancy, restoreOccupancy, type OccupancyMark, type ScatterOccupancy } from "./scatter-occupancy.ts";
import { COVER_CLEAR_MAX, STROKE_PROPS_MAX, clampedCentre, dabCentresTo, dabSeed, dabSpacing, startStroke, strokeSeed, strokeTo, type ScatterStroke, type StrokeInput } from "./scatter-stroke.ts";
import { scatterDab, type ScatterPlacement } from "./scatter.ts";
import { dabWhy, strokeWhy, type BrushWhy } from "./scatter-why.ts";
import type { PropRef } from "./templates.ts";

/**
 * The prop brush in the editor (ADR-123 decisions 2.6, 2.7, 4 to 7): a brush set in hand, prepared from the entries
 * the world can use; the radius and density, kept whichever set is held and never saved; the tab's stroke counter
 * the seeds come from; the ghosts a hovered cell shows, which are exactly the first dab a press there places; the
 * stroke, held as a preview and written at release as one command; and the erase, the set's props inside the
 * circle on the layers the set places on, removed at release as one command. View state outside history: the
 * set, radius, density and counter are never saved or undone. Points are in authoring cells, fractions allowed;
 * a dab's centre is the nearest cell corner on the map.
 */

/**
 * What a stroke that has placed or taken nothing returns, one array each.
 */
const NO_PIECES: readonly BrushGhost[] = [];
const NO_REFS: readonly PropRef[] = [];

/**
 * The radius a tab starts with, in tiles.
 */
export const DEFAULT_BRUSH_RADIUS = 8;

/**
 * The density a tab starts with (decision 2.4's proposal).
 */
export const DEFAULT_BRUSH_DENSITY = 40;

/**
 * What the brush needs from the store.
 */
export interface PropBrushHost {
  readonly document: () => WorldDocument;
  readonly map: () => MapDocument;
  readonly mapId: () => string;
  readonly activeLayerId: () => string | null;
  readonly isEditable: (layerId: string) => boolean;
  readonly lookups: () => FrameLookups;
  readonly allocateIds: (prefix: string, count: number) => string[];
  readonly allocateId: (prefix: string) => string;
  readonly dropTemplate: () => void;
  readonly guardDelete: (things: readonly ReferenceThing[], run: () => boolean) => boolean;
  readonly referencedIds: () => ReadonlySet<string>;
  readonly run: (command: Command) => boolean;
  readonly notify: () => void;
}

/**
 * The set in hand: its id, the set as read, and the set prepared for the world open when it was taken.
 */
export interface HeldBrush {
  readonly id: string;
  readonly set: BrushSet;
  readonly prepared: HeldKit;
  readonly kit: ScatterKit;
}

/**
 * What a released stroke did: how many props it placed and whether it stopped at its cap, how many it erased, or
 * how many an erase asks about, its removal waiting on the reference question (`asked`): those are reported to
 * `onErased` once the question is answered with Delete, and never when it is cancelled. A stroke that cleared
 * ground cover says how many props it removed in `cleared`, which is left out when none went; one that placed
 * little says why in `why` (ADR-123's amendment of 2026-10-13, A4), left out when it has nothing to say.
 */
export type BrushOutcome = { readonly kind: "placed"; readonly count: number; readonly stopped: boolean; readonly cleared?: number; readonly why?: BrushWhy } | { readonly kind: "erased"; readonly count: number } | { readonly kind: "asked"; readonly count: number };

/**
 * A stroke in progress: whether it erases, the layer it was begun on, the scatter's stroke and the occupancy with
 * the mark to put it back to on a cancel, or the erase's last centre and the props it has taken (by key, and in
 * the order taken); and the pieces its placements draw, each appended once as its placement is made.
 */
interface RunningStroke {
  readonly erase: boolean;
  readonly layerId: string;
  readonly map: MapDocument;
  readonly scatter: ScatterStroke | null;
  readonly occupancy: ScatterOccupancy | null;
  readonly mark: OccupancyMark | null;
  last: { x: number; y: number } | null;
  readonly erased: Map<string, PropRef>;
  readonly erasedList: PropRef[];
  readonly pieces: BrushGhost[];
}

/**
 * The hover's occupancy, kept while the map and the kit are the same objects, and the last ghosts it gave.
 */
interface HoverCache {
  readonly map: MapDocument;
  readonly kit: ScatterKit;
  readonly editable: string;
  readonly occupancy: ScatterOccupancy;
  key: string;
  ghosts: readonly BrushGhost[];
  cleared: readonly PropRef[];
  why: BrushWhy | null;
}

/**
 * The prop brush's slice.
 */
export type PropBrushSlice = Pick<PropBrush, "held" | "hold" | "drop" | "radius" | "setRadius" | "density" | "setDensity" | "counter" | "reroll" | "applies" | "ghosts" | "clearedAt" | "whyAt" | "erasable" | "begin" | "extend" | "stroking" | "erasing" | "strokeGhosts" | "strokeCleared" | "strokeErased" | "release" | "cancel" | "onErased" | "bindArt" | "bindCover" | "entryCover" | "coverSetsOf">;

/**
 * Whether two sets hold the same keys.
 *
 * @param a One set.
 * @param b The other.
 * @returns True when they are equal.
 */
function sameKeys(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) {
    return false;
  }
  for (const key of a) {
    if (!b.has(key)) {
      return false;
    }
  }
  return true;
}

/**
 * The brush.
 */
export class PropBrush {
  private hand: HeldBrush | null = null;
  private radiusTiles = DEFAULT_BRUSH_RADIUS;
  private densityValue = DEFAULT_BRUSH_DENSITY;
  private strokes = 1;
  private running: RunningStroke | null = null;
  private hover: HoverCache | null = null;
  private readonly erasedListeners = new Set<(count: number) => void>();
  private art: FrameLookups["artOf"] | null = null;
  private sets: readonly BrushSet[] = [];
  private handLookup: BrushSetLookup | null = null;

  /**
   * @param host The store's view of itself.
   */
  public constructor(private readonly host: PropBrushHost) {}

  /**
   * Binds what the asset manifest says of each frame's art, which gives a prop with no footprint its base (ADR-123's
   * amendment of 2026-10-13, A1a.1). Unbound, every frame has its foot cell alone.
   *
   * @param artOf The lookup.
   */
  public bindArt(artOf: FrameLookups["artOf"]): void {
    this.art = artOf;
  }

  /**
   * Binds the sets the tab can read, whose frames of ground cover no brush stands in the way of (ADR-123's amendment
   * of 2026-10-13, A2.2). The set in hand is prepared again with the new cover before its next hover or stroke; a
   * stroke already running keeps the cover it began with. Call it again when the library's list reloads.
   *
   * @param sets The readable sets, in any order; a set the tab cannot read is left out by the caller.
   */
  public bindCover(sets: readonly BrushSet[]): void {
    this.sets = sets;
    this.refreshKit();
  }

  /**
   * Prepares the set in hand again when the cover it holds differs from the cover known now, unless a stroke is
   * running. The cover is recomputed each time, so a footprint changed in the library since the set was prepared is
   * seen at the next hover or press (A2.2). The hover's cache goes with the old kit.
   */
  private refreshKit(): void {
    const hand = this.hand;
    if (this.running !== null || hand === null || this.handLookup === null) {
      return;
    }
    const cover = knownCover(this.sets, this.lookups());
    if (sameKeys(cover, hand.kit.cover)) {
      return;
    }
    const prepared = brushKit(hand.set, this.handLookup, this.lookups(), this.host.document().tileSize, undefined, cover);
    if (prepared.kit !== null) {
      this.hand = { id: hand.id, set: hand.set, prepared, kit: prepared.kit };
      this.hover = null;
    }
  }

  /**
   * Whether an entry is ground cover as its set says, for the panel's tick (A2.1).
   *
   * @param entry The entry.
   * @returns Its resolved state.
   */
  public entryCover(entry: BrushEntry): boolean {
    return entryCover(entry, this.lookups());
  }

  /**
   * The names of the sets that make a frame ground cover, for the panel's "Also ground cover in".
   *
   * @param sets The sets to read, named.
   * @param frame The frame.
   * @returns The names, in the order given.
   */
  public coverSetsOf(sets: readonly { readonly name: string; readonly entries: readonly BrushEntry[] }[], frame: TileRef): string[] {
    return coverSetsOf(sets, frame, this.lookups());
  }

  /**
   * The frame lookups the host gives, with the bound art in place of its own.
   *
   * @returns The lookups.
   */
  private lookups(): FrameLookups {
    const lookups = this.host.lookups();
    return this.art === null ? lookups : { ...lookups, artOf: this.art };
  }

  /**
   * The set in hand, or null.
   *
   * @returns The set.
   */
  public get held(): HeldBrush | null {
    return this.hand;
  }

  /**
   * Takes a set into the hand, putting a template in hand down (decision 7.1). The set is prepared from the entries
   * the world can use (decision 1.5); a set with none is not taken.
   *
   * @param id The set's id.
   * @param set The set.
   * @param lookup The world's frames and the library's templates.
   * @param frameName Names a frame as the palettes do, for the refusal.
   * @returns Null once held, or why it cannot be: "Forest edge cannot be held: Oak tree is not in this world's packs".
   */
  public hold(id: string, set: BrushSet, lookup: BrushSetLookup, frameName: (frame: TileRef) => string): string | null {
    const prepared = brushKit(set, lookup, this.lookups(), this.host.document().tileSize, undefined, knownCover(this.sets, this.lookups()));
    if (prepared.kit === null) {
      return `${set.name} cannot be held: ${firstSkipReason(set, prepared.flags, frameName) ?? "it has no entries"}`;
    }
    this.cancelStroke();
    this.hand = { id, set, prepared, kit: prepared.kit };
    this.handLookup = lookup;
    this.hover = null;
    this.host.dropTemplate();
    this.host.notify();
    return null;
  }

  /**
   * Puts the set down, cancelling a running stroke.
   */
  public drop(): void {
    if (this.hand === null) {
      return;
    }
    this.cancelStroke();
    this.hand = null;
    this.hover = null;
    this.host.notify();
  }

  /**
   * The radius in tiles.
   *
   * @returns The radius.
   */
  public get radius(): number {
    return this.radiusTiles;
  }

  /**
   * Sets the radius, rounded and kept from `RADIUS_MIN` to `RADIUS_MAX`.
   *
   * @param radius The radius in tiles.
   */
  public setRadius(radius: number): void {
    const next = Math.min(RADIUS_MAX, Math.max(RADIUS_MIN, Math.round(radius)));
    if (next !== this.radiusTiles) {
      this.radiusTiles = next;
      this.host.notify();
    }
  }

  /**
   * The density, from `DENSITY_MIN` (sparse) to `DENSITY_MAX` (dense).
   *
   * @returns The density.
   */
  public get density(): number {
    return this.densityValue;
  }

  /**
   * Sets the density, rounded and kept within the slider's bounds.
   *
   * @param density The density.
   */
  public setDensity(density: number): void {
    const next = Math.min(DENSITY_MAX, Math.max(DENSITY_MIN, Math.round(density)));
    if (next !== this.densityValue) {
      this.densityValue = next;
      this.host.notify();
    }
  }

  /**
   * The tab's stroke counter, from 1 (decision 2.6).
   *
   * @returns The counter.
   */
  public get counter(): number {
    return this.strokes;
  }

  /**
   * Gives the next arrangement without painting (Reroll).
   */
  public reroll(): void {
    this.strokes += 1;
    this.host.notify();
  }

  /**
   * Whether the brush acts on a press: a set is in hand and the active layer is an editable prop layer.
   *
   * @returns True when it does.
   */
  public applies(): boolean {
    const active = this.host.activeLayerId();
    return this.hand !== null && active !== null && this.host.map().layers.find((l) => l.id === active)?.kind === "prop" && this.host.isEditable(active);
  }

  /**
   * The ghosts a press at a point would place: the first dab of a stroke begun there, with the same seed, occupancy
   * and target (decision 5.2). The occupancy is built once for the map and the set and put back after each dab;
   * the result is kept until the centre, the radius, the density, the counter, the map or the set changes.
   *
   * @param x The pointer's column, in cells.
   * @param y The pointer's row, in cells.
   * @returns The pieces, empty when the brush does not apply.
   */
  public ghosts(x: number, y: number): readonly BrushGhost[] {
    this.refreshKit();
    const hand = this.hand;
    const layerId = this.host.activeLayerId();
    if (hand === null || layerId === null || !this.applies()) {
      return [];
    }
    const map = this.host.map();
    const centre = clampedCentre(x, y, map, hand.kit.cellsPerTile);
    const key = `${centre.x} ${centre.y} ${this.radiusTiles} ${this.densityValue} ${this.strokes}`;
    const editable = this.editableKey(map, hand.kit);
    let cache = this.hover;
    if (cache === null || cache.map !== map || cache.kit !== hand.kit || cache.editable !== editable) {
      cache = this.newHover(map, hand.kit, editable);
      this.hover = cache;
    } else if (cache.key === key) {
      return cache.ghosts;
    }
    const mark = markOccupancy(cache.occupancy);
    const room = startStroke(map.seed, this.strokes, this.layerCount(map, layerId)).room;
    const dab = scatterDab({ ...this.strokeInput(hand, map), centreX: centre.x, centreY: centre.y, seed: dabSeed(strokeSeed(map.seed, this.strokes), centre.x, centre.y), room, clearRoom: COVER_CLEAR_MAX }, cache.occupancy);
    restoreOccupancy(cache.occupancy, mark);
    cache.key = key;
    cache.ghosts = ghostPieces(hand.kit, dab.placements);
    cache.cleared = dab.cleared;
    cache.why = dabWhy(dab);
    return cache.ghosts;
  }

  /**
   * The ground cover a press at a point would clear: what the first dab of a stroke begun there removes, the same dab
   * as the ghosts (decision 5.2), for the tint the preview draws over it (A3.6).
   *
   * @param x The pointer's column, in cells.
   * @param y The pointer's row, in cells.
   * @returns The props, empty when the brush does not apply or the set does not clear.
   */
  public clearedAt(x: number, y: number): readonly PropRef[] {
    return this.ghosts(x, y).length === 0 || this.hover === null ? NO_REFS : this.hover.cleared;
  }

  /**
   * Why a press at a point would place little: the reason of the first dab of a stroke begun there, the same dab as
   * the ghosts (decision 5.2), for the status strip while the pointer rests.
   *
   * @param x The pointer's column, in cells.
   * @param y The pointer's row, in cells.
   * @returns The reason, or null when the brush does not apply or has nothing to say.
   */
  public whyAt(x: number, y: number): BrushWhy | null {
    this.ghosts(x, y);
    return this.hover === null || !this.applies() ? null : this.hover.why;
  }

  /**
   * A hover cache for a map and a kit: the occupancy that knows which cover may be cleared, never a prop that
   * carries a behaviour or that a reference field names.
   *
   * @param map The map.
   * @param kit The kit.
   * @param editable What `editableKey` gave for them.
   * @returns The cache, with no ghosts yet.
   */
  private newHover(map: MapDocument, kit: ScatterKit, editable: string): HoverCache {
    return { map, kit, editable, occupancy: createOccupancy(map, this.host.document().tileSize, kit, this.lookups(), (id) => this.host.isEditable(id), kit.clearsGroundCover ? this.host.referencedIds() : undefined), key: "", ghosts: [], cleared: [], why: null };
  }

  /**
   * Which prop layers' cover a set that clears may clear, as a key: the editable prop layers' ids. A set that clears
   * nothing has the empty key, so a lock changing under it costs it no new occupancy.
   *
   * @param map The map.
   * @param kit The kit.
   * @returns The key.
   */
  private editableKey(map: MapDocument, kit: ScatterKit): string {
    if (!kit.clearsGroundCover) {
      return "";
    }
    let key = "";
    for (const layer of map.layers) {
      if (layer.kind === "prop" && this.host.isEditable(layer.id)) {
        key += `${layer.id}\u0000`;
      }
    }
    return key;
  }

  /**
   * The set's props an erase press at a point would take (decision 5.1's tint).
   *
   * @param x The pointer's column, in cells.
   * @param y The pointer's row, in cells.
   * @returns The props, empty when the brush does not apply.
   */
  public erasable(x: number, y: number): readonly PropRef[] {
    const hand = this.hand;
    const layerId = this.host.activeLayerId();
    if (hand === null || layerId === null || !this.applies()) {
      return [];
    }
    const map = this.host.map();
    const centre = clampedCentre(x, y, map, hand.kit.cellsPerTile);
    return erasableIn(map, hand.kit, eraseLayers(map, layerId, hand.prepared.templates, this.host.isEditable), centre.x, centre.y, this.radiusTiles, this.lookups());
  }

  /**
   * Begins a stroke at a point, placing its first dab or taking the props of its first erase dab. Whether it erases
   * is fixed here for the whole stroke (decision 2.7).
   *
   * @param x The pointer's column, in cells.
   * @param y The pointer's row, in cells.
   * @param erase Whether the stroke erases.
   * @returns False when the brush does not apply, so the press is the paint tool's as before.
   */
  public begin(x: number, y: number, erase: boolean): boolean {
    this.refreshKit();
    const hand = this.hand;
    const layerId = this.host.activeLayerId();
    if (hand === null || layerId === null || !this.applies()) {
      return false;
    }
    this.cancelStroke();
    const map = this.host.map();
    if (erase) {
      this.running = { erase, layerId, map, scatter: null, occupancy: null, mark: null, last: null, erased: new Map(), erasedList: [], pieces: [] };
    } else {
      const editable = this.editableKey(map, hand.kit);
      if (this.hover === null || this.hover.map !== map || this.hover.kit !== hand.kit || this.hover.editable !== editable) {
        this.hover = this.newHover(map, hand.kit, editable);
      }
      const occupancy = this.hover.occupancy;
      this.hover.key = "";
      this.running = { erase, layerId, map, scatter: startStroke(map.seed, this.strokes, this.layerCount(map, layerId)), occupancy, mark: markOccupancy(occupancy), last: null, erased: new Map(), erasedList: [], pieces: [] };
    }
    this.extend(x, y);
    return true;
  }

  /**
   * Moves a running stroke's pointer: a dab at each spacing along the way from the last dab's centre.
   *
   * @param x The pointer's column, in cells.
   * @param y The pointer's row, in cells.
   */
  public extend(x: number, y: number): void {
    const running = this.running;
    const hand = this.hand;
    if (running === null || hand === null) {
      return;
    }
    if (running.scatter !== null && running.occupancy !== null) {
      const from = running.scatter.placements.length;
      strokeTo(running.scatter, { x, y }, this.strokeInput(hand, running.map), running.occupancy);
      appendGhostPieces(hand.kit, running.scatter.placements, from, running.pieces);
      return;
    }
    const cellsPerTile = hand.kit.cellsPerTile;
    const at = clampedCentre(x, y, running.map, cellsPerTile);
    const centres = running.last === null ? [at] : dabCentresTo(running.last, at, dabSpacing(this.radiusTiles, cellsPerTile));
    const layers = eraseLayers(running.map, running.layerId, hand.prepared.templates, this.host.isEditable);
    for (const centre of centres) {
      running.last = centre;
      for (const ref of erasableIn(running.map, hand.kit, layers, centre.x, centre.y, this.radiusTiles, this.lookups())) {
        if (running.erased.size >= STROKE_PROPS_MAX) {
          return;
        }
        const key = `${ref.layerId}\u0000${ref.propId}`;
        if (!running.erased.has(key)) {
          running.erased.set(key, ref);
          running.erasedList.push(ref);
        }
      }
    }
  }

  /**
   * Whether a stroke is running.
   *
   * @returns True between a begin and its release or cancel.
   */
  public get stroking(): boolean {
    return this.running !== null;
  }

  /**
   * Whether the running stroke erases.
   *
   * @returns True for an erase stroke.
   */
  public get erasing(): boolean {
    return this.running?.erase === true;
  }

  /**
   * The pieces the running stroke has placed so far, drawn at full opacity (decision 5.2): one array for the
   * stroke, grown as it moves, so a redraw allocates nothing.
   *
   * @returns The pieces, empty for none or an erase.
   */
  public strokeGhosts(): readonly BrushGhost[] {
    return this.running?.pieces ?? NO_PIECES;
  }

  /**
   * The ground cover the running stroke has cleared so far, in the order cleared: one array for the stroke, grown as
   * it moves, for the tint drawn over it.
   *
   * @returns The props, empty for none or an erase.
   */
  public strokeCleared(): readonly PropRef[] {
    return this.running?.scatter?.cleared ?? NO_REFS;
  }

  /**
   * The props the running erase has taken so far, in the order taken: one array for the stroke, grown as it moves.
   *
   * @returns The props.
   */
  public strokeErased(): readonly PropRef[] {
    return this.running?.erasedList ?? NO_REFS;
  }

  /**
   * Ends the running stroke and writes it as one command: the placements as one compound (decision 6.1), or the
   * erased props as one removal (decision 4.4). The counter then moves on.
   *
   * @returns What it did, or null when no stroke ran or the store refused the command.
   */
  public release(): BrushOutcome | null {
    const running = this.running;
    const hand = this.hand;
    this.running = null;
    if (running === null || hand === null) {
      return null;
    }
    this.strokes += 1;
    if (running.occupancy !== null && running.mark !== null) {
      restoreOccupancy(running.occupancy, running.mark);
    }
    if (running.erase) {
      const refs = running.erasedList;
      if (refs.length === 0) {
        this.host.notify();
        return { kind: "erased", count: 0 };
      }
      const pending = { asked: false, ran: false };
      const held = this.host.guardDelete(
        refs.map((ref) => ({ kind: "prop", layerId: ref.layerId, propId: ref.propId })),
        () => {
          const command = removePropsCommand(this.host.map(), refs);
          pending.ran = command !== null && this.host.run(command);
          if (pending.ran && pending.asked) {
            for (const listener of this.erasedListeners) {
              listener(refs.length);
            }
          }
          return pending.ran;
        },
      );
      if (pending.ran) {
        return { kind: "erased", count: refs.length };
      }
      if (!held) {
        return null;
      }
      pending.asked = true;
      return { kind: "asked", count: refs.length };
    }
    const scatter = running.scatter as ScatterStroke;
    const placements: readonly ScatterPlacement[] = scatter.placements;
    const stopped = scatter.stopped === "cap";
    const why = strokeWhy(scatter);
    const pieces = placements.reduce((sum, p) => sum + (hand.kit.entries[p.entry]?.pieces.length ?? 0), 0);
    const pool = this.host.allocateIds("prop", pieces);
    let next = 0;
    const written = strokeCommand(this.host.document(), this.host.mapId(), running.layerId, hand.kit, hand.prepared.templates, placements, (prefix) => (prefix === "prop" && next < pool.length ? (pool[next++] as string) : this.host.allocateId(prefix)), scatter.cleared, scatter.cleared.length > 0 ? this.host.referencedIds() : undefined);
    if (written === null) {
      this.host.notify();
      return { kind: "placed", count: 0, stopped, ...(why === null ? {} : { why }) };
    }
    return this.host.run(written.command) ? { kind: "placed", count: written.props.length, stopped, ...(written.cleared > 0 ? { cleared: written.cleared } : {}), ...(why === null ? {} : { why }) } : null;
  }

  /**
   * Listens for erases removed once the reference question they waited on is answered with Delete.
   *
   * @param listener Called with how many props were removed.
   * @returns Unsubscribe function.
   */
  public onErased(listener: (count: number) => void): () => void {
    this.erasedListeners.add(listener);
    return () => {
      this.erasedListeners.delete(listener);
    };
  }

  /**
   * Drops the running stroke unwritten (Escape); the counter moves on.
   */
  public cancel(): void {
    if (this.running === null) {
      return;
    }
    this.cancelStroke();
    this.strokes += 1;
    this.host.notify();
  }

  /**
   * Drops the running stroke, putting the occupancy back as it was before the stroke began.
   */
  private cancelStroke(): void {
    const running = this.running;
    this.running = null;
    if (running?.occupancy !== null && running?.occupancy !== undefined && running.mark !== null) {
      restoreOccupancy(running.occupancy, running.mark);
    }
  }

  /**
   * What every dab of the set shares over a map.
   *
   * @param hand The set in hand.
   * @param map The map.
   * @returns The input.
   */
  private strokeInput(hand: HeldBrush, map: MapDocument): StrokeInput {
    const document = this.host.document();
    return { kit: hand.kit, map, tilesets: document.tilesets, world: document.id, radius: this.radiusTiles, density: this.densityValue };
  }

  /**
   * How many props a layer holds.
   *
   * @param map The map.
   * @param layerId The layer.
   * @returns The count.
   */
  private layerCount(map: MapDocument, layerId: string): number {
    const layer = map.layers.find((l) => l.id === layerId);
    return layer?.kind === "prop" ? layer.props.length : 0;
  }
}
